<script module lang="ts">
	import type { LoaderType } from "../types.js";
	import {
		DEFAULT_ANIMATION_DURATION_CLASS,
		DEFAULT_COMPOUND_HOVER_VARIANTS,
		DEFAULT_VARIANTS,
		getAnimationDuration
	} from "../Utils.js";

	const buttonClasses = cva(
		[
			"duration-150",
			"inline-flex",
			"items-center",
			"justify-center",
			"gap-2",
			"leading-4",
			"overflow-hidden",
			"relative",
			"rounded-base",
			"shadow-md",
			"transition-all"
		],
		{
			compoundVariants: [
				...(DEFAULT_COMPOUND_HOVER_VARIANTS as any),
				{ class: "active:scale-95", disabled: false, groupType: undefined }
			],
			defaultVariants: { disabled: false, size: "default", variant: "default" },
			variants: {
				disabled: {
					false: ["cursor-pointer"],
					true: ["cursor-not-allowed", "opacity-50"]
				},
				grouping: {
					top: ["rounded-b-none", "border", "border-gray-400"],
					bottom: ["rounded-t-none", "border", "border-gray-400"],
					left: ["rounded-r-none", "border", "border-gray-400"],
					right: ["rounded-l-none", "border", "border-gray-400"],
					center: ["rounded-none", "border", "border-gray-400"],
					none: undefined
				},
				iconPosition: { left: undefined, right: "flex-row-reverse" },
				groupType: {
					input: [
						"rounded-none",
						"first:rounded-r-none",
						"first:rounded-l-lg",
						"last:rounded-r-lg",
						"last:rounded-l-none",
						"shadow-none",
						"border",
						"border-white/25"
					]
				},
				size: {
					default: ["px-3", "py-2"],
					lg: ["px-7", "py-4"],
					md: ["px-5", "py-3"],
					plain: undefined,
					sm: ["px-1", "py-1"]
				},
				variant: DEFAULT_VARIANTS
			}
		}
	);
	export interface ButtonProps {
		class?: string;
		icon?: string | IconDefinition;
		iconPosition?: "left" | "right";
		loading?: boolean;
		loaderType?: LoaderType;
		size?: VariantProps<typeof buttonClasses>["size"];
		variant?: VariantProps<typeof buttonClasses>["variant"];
		tooltip?: string;
		grouping?: "top" | "bottom" | "left" | "right" | "center" | "none";
		tooltipIconStyle?: string;
		stopPropagation?: boolean;
		disabled?: boolean;
		children?: Snippet<[]>;
	}
</script>

<script lang="ts">
	import type { IconDefinition } from "@fortawesome/free-solid-svg-icons";
	import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
	import { cva, type VariantProps } from "class-variance-authority";
	import { getContext, type Snippet } from "svelte";
	import type { HTMLButtonAttributes } from "svelte/elements";
	import { fade } from "svelte/transition";
	import { twMerge } from "tailwind-merge";
	import Icon from "../Icon/Icon.svelte";
	import Loader from "../Loader/Loader.svelte";
	import { tooltip as tooltipInternal } from "../Tooltip/openTooltip.svelte.js";

	//   const variantCtx  = getContext<ButtonProps["variant"]>("variant_ctx");
	const groupType = getContext<VariantProps<typeof buttonClasses>["groupType"]>("group_ctx");

	let {
		disabled,
		icon,
		iconPosition,
		loaderType,
		loading,
		size,
		title,
		grouping,
		tooltipIconStyle,
		tooltip,
		type,
		variant,
		//  stopPropagation = true,
		children,
		...restProps
	}: ButtonProps & HTMLButtonAttributes = $props();

	let classes = $derived(
		twMerge(
			buttonClasses({
				class: restProps.class,
				disabled: disabled || loading,
				size,
				variant,
				grouping,
				groupType,
				iconPosition
			})
		)
	);
</script>

{#if tooltip}
	<div class="relative z-50 -mb-2 w-[20px]" title={tooltip} use:tooltipInternal>
		<Icon class={tooltipIconStyle} icon={faInfoCircle} />
	</div>

	<button {...restProps} class={classes} disabled={disabled || loading} title={title} type={type}>
		{#if loading}
			<div
				class="absolute top-0 right-0 left-0 z-10 flex h-full w-full items-center justify-center rounded-[inherit]"
				in:fade={{
					duration: getAnimationDuration(DEFAULT_ANIMATION_DURATION_CLASS)
				}}
				out:fade={{
					duration: getAnimationDuration(DEFAULT_ANIMATION_DURATION_CLASS)
				}}
			>
				<Loader class={loaderType === "dots" ? "-mt-3" : ""} type={loaderType || "circle"} />
			</div>
		{/if}

		{#if icon}
			<Icon class={loading ? "text-transparent" : ""} icon={icon} />
		{/if}

		{#if children}
			<div class:text-transparent={loading}>
				{@render children()}
			</div>
		{/if}
	</button>
{:else}
	<button {...restProps} class={classes} disabled={disabled || loading} title={title} type={type}>
		{#if loading}
			<div
				class="absolute top-0 right-0 left-0 z-10 flex h-full w-full items-center justify-center rounded-[inherit]"
				in:fade={{
					duration: getAnimationDuration(DEFAULT_ANIMATION_DURATION_CLASS)
				}}
				out:fade={{
					duration: getAnimationDuration(DEFAULT_ANIMATION_DURATION_CLASS)
				}}
			>
				<Loader class={loaderType === "dots" ? "-mt-3" : ""} type={loaderType || "circle"} />
			</div>
		{/if}

		{#if icon}
			<Icon class={loading ? "text-transparent" : ""} icon={icon} />
		{/if}

		{#if children}
			<div class:text-transparent={loading}>
				{@render children()}
			</div>
		{/if}
	</button>
{/if}
